import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSupabaseClient, verifyOtpCode } from '../lib/supabase';
import { de } from '../lib/i18n/de';

type CallbackState =
  | { kind: 'exchanging' }
  | { kind: 'success' }
  | { kind: 'error'; message: string; isPkceMismatch: boolean };

/**
 * Magic-Link-Callback.
 *
 * Supabase leitet den User nach Klick auf den Magic-Link zu dieser Route
 * mit `?code=...&type=magiclink` (PKCE-Flow). Wir tauschen den Code gegen
 * eine Session ein und navigieren weiter zu `/cases` (oder zu dem
 * `redirect`-Param, falls gesetzt).
 *
 * Fehlerpfade:
 *   - Code fehlt → klare Meldung + „Erneut anfordern".
 *   - PKCE-Verifier fehlt (Mail in anderem Browser geöffnet) → Inline-
 *     Recovery-Form: User gibt E-Mail + 6-stelligen Code aus derselben
 *     Mail ein, wir nutzen den OTP-Pfad (verifyOtpCode), der PKCE umgeht.
 *   - Token abgelaufen / ungültig → Error-Message von Supabase wird
 *     gerendert, User kann zurück zum Login.
 *
 * Diese Komponente wird in Dev (Mock-Auth) nie aufgerufen, weil der
 * Mock-Login direkt authentifiziert. In Production ist sie der einzige
 * Auth-Eintrittspunkt für Magic-Link-Sessions.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState<CallbackState>({ kind: 'exchanging' });

  // Recovery-Form-State (nur aktiv, wenn state.kind === 'error' && isPkceMismatch)
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryOtp, setRecoveryOtp] = useState('');
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const code = params.get('code');
      const errorDescription = params.get('error_description');

      if (errorDescription) {
        setState({ kind: 'error', message: errorDescription, isPkceMismatch: false });
        return;
      }

      if (!code) {
        setState({
          kind: 'error',
          message: de.authCallback.missingCode,
          isPkceMismatch: false,
        });
        return;
      }

      try {
        const supa = getSupabaseClient();
        const { error } = await supa.auth.exchangeCodeForSession(code);
        if (cancelled) return;
        if (error) {
          // PKCE-Mismatch erkennen — typische Meldungen enthalten "code verifier"
          // oder "verifier not found". Wir setzen das Flag, damit unten die
          // Recovery-Form gerendert wird.
          const msg = error.message.toLowerCase();
          const isPkce = msg.includes('verifier') || msg.includes('pkce');
          setState({ kind: 'error', message: error.message, isPkceMismatch: isPkce });
          return;
        }
        setState({ kind: 'success' });
        setTimeout(() => {
          if (!cancelled) {
            const redirect = params.get('redirect') ?? '/cases';
            void navigate(redirect, { replace: true });
          }
        }, 600);
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : de.authCallback.unknownError,
          isPkceMismatch: false,
        });
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [navigate, params]);

  const handleRecoverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryError(null);
    setRecoverySubmitting(true);
    try {
      const emailTrimmed = recoveryEmail.trim().toLowerCase();
      const otpTrimmed = recoveryOtp.trim();
      if (!emailTrimmed.includes('@')) {
        setRecoveryError(de.authCallback.pkceEmailInvalid);
        return;
      }
      if (!/^\d{6}$/.test(otpTrimmed)) {
        setRecoveryError(de.authCallback.pkceOtpInvalid);
        return;
      }
      const res = await verifyOtpCode(emailTrimmed, otpTrimmed);
      if (!res.ok) {
        setRecoveryError(res.error ?? de.authCallback.pkceVerifyFailed);
        return;
      }
      const redirect = params.get('redirect') ?? '/cases';
      void navigate(redirect, { replace: true });
    } catch (err) {
      setRecoveryError(err instanceof Error ? err.message : de.authCallback.unknownError);
    } finally {
      setRecoverySubmitting(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-[60vh] max-w-prose flex-col justify-center px-5 py-16 md:px-8">
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.authCallback.sectionLabel}
      </p>
      <h1 className="mt-3 font-serif text-4xl font-black leading-tight tracking-tight text-stone-900 md:text-5xl">
        {state.kind === 'exchanging' && de.authCallback.exchangingTitle}
        {state.kind === 'success' && de.authCallback.successTitle}
        {state.kind === 'error' &&
          (state.isPkceMismatch ? de.authCallback.pkceTitle : de.authCallback.errorTitle)}
      </h1>

      {state.kind === 'exchanging' && (
        <p className="mt-4 font-serif text-base italic text-stone-500">
          {de.authCallback.exchangingBody}
        </p>
      )}

      {state.kind === 'success' && (
        <p className="mt-4 font-serif text-base italic text-stone-500">
          {de.authCallback.successBody}
        </p>
      )}

      {state.kind === 'error' && state.isPkceMismatch && (
        <>
          <p className="mt-4 font-serif text-base text-stone-700">
            {de.authCallback.pkceExplainer1}
          </p>
          <p className="mt-3 font-serif text-base text-stone-700">
            <strong>{de.authCallback.pkceExplainer2Strong}</strong>
            {de.authCallback.pkceExplainer2Rest}
          </p>

          <form
            onSubmit={(e) => void handleRecoverySubmit(e)}
            className="mt-8 rounded-sm border border-stone-200 bg-white p-6 md:p-8"
            noValidate
          >
            <div>
              <label
                htmlFor="recovery-email"
                className="block font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
              >
                {de.authCallback.pkceEmailLabel}
              </label>
              <input
                id="recovery-email"
                type="email"
                required
                autoComplete="email"
                value={recoveryEmail}
                onChange={(e) => {
                  setRecoveryEmail(e.target.value);
                }}
                placeholder="name@beispiel.de"
                className="mt-3 w-full rounded-sm border border-stone-200 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder-stone-300 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
              />
            </div>

            <div className="mt-6">
              <label
                htmlFor="recovery-otp"
                className="block font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
              >
                {de.authCallback.pkceOtpLabel}
              </label>
              <input
                id="recovery-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={recoveryOtp}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setRecoveryOtp(digits);
                }}
                placeholder="123456"
                className="mt-3 w-full max-w-[12rem] rounded-sm border border-stone-200 bg-white px-4 py-3 text-center font-mono text-lg tracking-[0.4em] text-stone-900 placeholder-stone-300 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
              />
            </div>

            {recoveryError && (
              <p
                role="alert"
                className="mt-4 font-sans text-xs font-medium uppercase tracking-widest text-rose-600"
              >
                {recoveryError}
              </p>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={
                  recoverySubmitting ||
                  recoveryEmail.trim().length === 0 ||
                  recoveryOtp.length !== 6
                }
                className="inline-flex items-center justify-center rounded-sm bg-stone-900 px-6 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-stone-300"
              >
                {recoverySubmitting
                  ? de.authCallback.pkceSubmitting
                  : `${de.authCallback.pkceSubmit} →`}
              </button>
              <a
                href="/login"
                className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500 underline hover:text-stone-900 hover:no-underline"
              >
                {de.authCallback.requestNewLink}
              </a>
            </div>
          </form>

          <details className="mt-6">
            <summary className="cursor-pointer font-sans text-xs font-medium uppercase tracking-widest text-stone-400 hover:text-stone-600">
              {de.authCallback.technicalDetails}
            </summary>
            <p className="mt-2 font-serif text-xs italic text-stone-500">{state.message}</p>
          </details>
        </>
      )}

      {state.kind === 'error' && !state.isPkceMismatch && (
        <>
          <p className="mt-4 font-serif text-base text-red-700">{state.message}</p>
          <a
            href="/login"
            className="mt-6 inline-flex w-fit rounded-sm bg-stone-900 px-4 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800"
          >
            {de.authCallback.backToLogin}
          </a>
        </>
      )}
    </main>
  );
}
